import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import _ from 'lodash';

import { getScreenWidth, generateKey } from '@/utils';
import { TABLET_P } from '@/constants';
import Slick from '@/components/Slider/Slick';
import Image from '../../components/ContentfulImage';
import Slider from '../../components/Slider/Slider';
import { contentTypeToSlug } from '../../pages/RangePage/rangeNameConverter';

import {
  CategoryText,
  CTA,
  ImageWrapper,
  ProductSlide,
  ProductNameText,
  RelatedProductsContainer,
  Section,
  Title,
} from './ProductRecommendation.style';
import Arrow from './Arrow';
import Placeholder from '../Placeholder';

/**
 * TOP > PRODUCTS > カテゴリ > LINE 画面に表示するラインナップ (カルーセル)
 */
const Product = ({ product, locale, copy, external }) => {
  const isEnabled = _.get(product, 'slug') && locale;
  const Anchor = props => external ? <a {...props} target="_blank">{props.children}</a> : <Link {...props}>{props.children}</Link>;

  return (
    <Anchor
      to={{
        pathname: isEnabled ? `/${locale}/product/${product.slug}` : null,
      }}
      href={locale && product ? `/${locale}/product/${product.slug}` : null}
    >
      <ImageWrapper>
        {product && product.variants && product.variants[0].image ? (
          <Image src={product.variants[0].image} marginSet="0" options={{ h: '100%', w: '100%', objectFit: 'contain', size: 160 }} />
        ) : (
          <Placeholder h="100%" w="100%" />
        )}
      </ImageWrapper>
      <CategoryText>{!!product && product.range ? product.range.name : <Placeholder h="20px" w="100%" />}</CategoryText>
      <ProductNameText>{!!product && product.name || <Placeholder h="20px" w="100%" />}</ProductNameText>
      <CTA>{!!copy && copy.learn_more || <Placeholder w="100px" h="20px" />}</CTA>
    </Anchor>
  )};

Product.propTypes = {
  copy: PropTypes.shape().isRequired,
  locale: PropTypes.string.isRequired,
  product: PropTypes.shape({}).isRequired,
};

/**
 * TOP > PRODUCTS > カテゴリ画面に表示するラインナップ (カルーセル)
 */
const Range = ({ range, locale, copy, location, ancestorName, parentName }) => {
  const isEnabled = locale && location;
  return (
    <Link
      to={{
        pathname: `/${locale}/range/${contentTypeToSlug(range.contentType)}/${range.slug}`,
        state: {
          prevPath: _.get(location, 'pathname') ? location.pathname : null,
          // カテゴリ名を設定する (例) HAIR CARE)
          ancestor: ancestorName ? ancestorName.toUpperCase() : null,
          // サブカテゴリ名を設定する (例) SUBLIMIC)
          parent: parentName ? parentName.toUpperCase() : null,
          // サブカテゴリ配下に、LINE 階層があることを明確にする
          hasLine: true,
        },
      }}
      href={locale ? `/${locale}/range/${contentTypeToSlug(range.contentType)}/${range.slug}` : null}
    >
      <ImageWrapper>
        {!!(isEnabled && range.productImage) ? (
          <Image src={range.productImage} options={{ h: '100%', w: '100%', objectFit: 'contain', size: 160 }} />
        ) : (
          <Placeholder h="100%" w="100%" />
        )}
      </ImageWrapper>
      {range.subCategory && range.subCategory !== '' ? <CategoryText>{range.subCategory}</CategoryText> : null}
      <ProductNameText>{!!isEnabled && range.name || <Placeholder h="20px" w="100%" />}</ProductNameText>
      <CTA>{!!isEnabled && copy.learn_more || <Placeholder w="100px" h="20px" />}</CTA>
    </Link>
  )
};

Range.propTypes = {
  copy: PropTypes.shape().isRequired,
  locale: PropTypes.string.isRequired,
  range: PropTypes.shape().isRequired,
};

class ProductRecommendation extends Component {
  constructor(props) {
    super(props);
    this.slideRef = React.createRef();
  }

  componentWillReceiveProps(nextProps) {
    const { isStylePage } = this.props;

    if (!isStylePage && JSON.stringify(nextProps.relatedProducts) !== JSON.stringify(this.props.relatedProducts)) {
      setTimeout(() => {
        this.slideRef.current.updateCurrentSlide(0);
      }, 200);
    }
  }

  render() {
    const { copy, id, entryId, locale, relatedProducts, screenSize, screenSizeExact, location, ancestorName, parentName, isStylePage, variation, stylePageTitle } = this.props;

    // Slick スライダー > サムネイル表示数 (SP、PC 共通)
    const slidesToShowNumber = screenSizeExact > TABLET_P ? 3 : 1;
    // Slick スライダー > スワイプ毎のページング数 (SP、PC 共通)
    const swipeToSlideNumber = 1;

    // スライドの表示枚数 (STYLE PAGE の SP 版は、一枚ずつ)
    const perPage = !!(isStylePage && window.innerWidth < TABLET_P) ? 1 : !!(isStylePage && window.innerWidth > TABLET_P) ? 3 : Math.min(screenSize + 1, relatedProducts.length, 4);

    return (
      <Section id={entryId || null} className={isStylePage ? 'stylePage' : null} screenSizeExact={screenSizeExact}>

        {!isStylePage && relatedProducts && relatedProducts.length > 0 &&
          <Title>{copy.accompany_with || <Placeholder w="200px" h="18px" />}</Title>
        }

        <RelatedProductsContainer items={perPage}>
          {isStylePage ? (
            <Slick
              withVariation
              variation={variation}
              withTitle
              title={stylePageTitle}
              // スライダー設定
              //   1. dots: true = 丸ポチを表示する
              //   2. variableWidth: false = 横幅の制御を許容しない
              //   3. arrows: true = 前後ボタンを表示する
              //   4. swipeToSlide: スワイプ毎のページング数
              //   5. slidesToShow: サムネイル表示数
              options={{ dots: true, variableWidth: false, arrows: true, swipeToSlide: swipeToSlideNumber, slidesToShow: slidesToShowNumber }}
            >
              {relatedProducts.map((product, index) => (
                <ProductSlide key={generateKey('style-product', index)} isLast={false}>
                  <Product product={product} external locale={locale} copy={copy} location={location} />
                </ProductSlide>
              ))}
            </Slick>
          ) : (
            <Slider
              ref={this.slideRef}
              arrows
              arrowLeft={<Arrow left />}
              arrowRight={<Arrow right />}
              dots
              dotsStyle={{ bottom: -40 }}
              keys={relatedProducts.map(p => p.entryId || p.key)}
              options={{
                loop: false,
                perPage,
                draggable: true,
              }}
            >
              {relatedProducts.map((product, index) => (
                <ProductSlide key={product.entryId || product.key} isLast={relatedProducts.length === index + 1}>
                  {product.contentType === 'product' ? (
                    <Product product={product} locale={locale} copy={copy} location={location} />
                  ) : (
                    <Range range={product} locale={locale} copy={copy} location={location} ancestorName={ancestorName} parentName={parentName} />
                  )}
                </ProductSlide>
              ))}
            </Slider>
          )}
        </RelatedProductsContainer>
      </Section>
    );
  }

};

ProductRecommendation.defaultProps = {
  copy: {},
  id: false,
  entryId: undefined,
  relatedProducts: undefined,
  location: null,
  ancestorName: null,
  parentName: null,
  // STYLE PAGE 表示切り替え用
  isStylePage: false,
  screenSizeExact: null,
};

ProductRecommendation.propTypes = {
  copy: PropTypes.shape(),
  id: PropTypes.bool,
  entryId: PropTypes.string,
  locale: PropTypes.string.isRequired,
  relatedProducts: PropTypes.arrayOf(PropTypes.shape({})),
  screenSize: PropTypes.number.isRequired,
  location: PropTypes.shape({}),
  ancestorName: PropTypes.string,
  parentName: PropTypes.string,
  // STYLE PAGE > USE PRODUCTS タイトル
  stylePageTitle: PropTypes.string,
  // STYLE PAGE 表示切り替え用
  isStylePage: PropTypes.bool,
  // STYLE PAGE > Slick 用のバリエーション (例) STYLE PAGE のカラーバリエーション)
  variation: PropTypes.arrayOf(PropTypes.shape({})),
  // スクリーンサイズ
  screenSizeExact: PropTypes.number,
};

const mapState = state => ({
  locale: state.locale,
  screenSize: state.screenSize,
  // スクリーンサイズを実寸で取得する
  screenSizeExact: getScreenWidth(),
});

export default connect(mapState)(ProductRecommendation);



// WEBPACK FOOTER //
// ./src/components/ProductRecommendation/ProductRecommendation.js